import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
  Autocomplete,
  createFilterOptions,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Divider,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useSelector } from "react-redux";
import type { RootState } from "../store";
import Api from "../services/apis/setup";
import { VehicleOption } from "../utils/types";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

// Service types
const SERVICE_TYPES = [
  'Oil Change',
  'Filter Replacement',
  'Air Filter',
  'Brake Service',
  'Tyre Service',
  'Battery Service',
  'General Service',
  'Repair',
  'Wheel Alignment',
  'Cooling System',
  'Electrical',
  'Suspension',
  'Transmission',
  'Other'
];

// Single service item type
type ServiceItem = {
  id: string;
  serviceType: string;
  partName: string;
  partQuantity: number;
  unitPrice: number;
  totalPartCost: number;
  serviceDescription: string;
};

type ServiceDetails = {
  odo: string;
  lastService: string;
  nextOilChangeOdo: string;
  nextGreasingOdo: string;
  remarks: string;
  spareChangedDate: string;
  sparePreviousModifiedDate: string;
  partDetails: string;
  amountOfParts: string;
  description: string;
  nextOdoCheckPoint: string;
  changedBy: string;
  status: string;
  // New fields
  serviceItems: ServiceItem[];
  labourCost: string;
  totalServiceCost: number;
  nextServiceDueKm: string;
  nextServiceDueDate: string;
  mechanicName: string;
  serviceCenter: string;
};

const initialForm: ServiceDetails = {
  odo: '',
  lastService: "",
  nextOilChangeOdo: '',
  nextGreasingOdo: '',
  remarks: "",
  spareChangedDate: "",
  sparePreviousModifiedDate: "",
  partDetails: "",
  amountOfParts: '',
  description: "",
  nextOdoCheckPoint: '',
  changedBy: "",
  status: "initiated",
  // New fields
  serviceItems: [],
  labourCost: '0',
  totalServiceCost: 0,
  nextServiceDueKm: '',
  nextServiceDueDate: '',
  mechanicName: '',
  serviceCenter: ''
};

function AddServiceDetails() {
  const [form, setForm] = useState<ServiceDetails>(initialForm);
  const [selectedVehicle, setSelectedVehicle] = useState<any>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  const vehicleList = useSelector((state: RootState) => state.vehicle.vehicleList);
  const [addServiceHistoryApi] = Api.useAddServiceHistoryMutation();
  const navigate = useNavigate();

  // Generate unique ID for service items
  const generateId = () => Math.random().toString(36).substr(2, 9);

  // Add a new service item
  const addServiceItem = () => {
    const newItem: ServiceItem = {
      id: generateId(),
      serviceType: 'General Service',
      partName: '',
      partQuantity: 1,
      unitPrice: 0,
      totalPartCost: 0,
      serviceDescription: ''
    };
    setForm(prev => ({
      ...prev,
      serviceItems: [...prev.serviceItems, newItem]
    }));
    calculateTotal();
  };

  // Remove a service item
  const removeServiceItem = (id: string) => {
    setForm(prev => ({
      ...prev,
      serviceItems: prev.serviceItems.filter(item => item.id !== id)
    }));
    calculateTotal();
  };

  // Update a service item
  const updateServiceItem = (id: string, field: keyof ServiceItem, value: any) => {
    setForm(prev => {
      const updatedItems = prev.serviceItems.map(item => {
        if (item.id === id) {
          const updated = { ...item, [field]: value };
          // Auto-calculate total part cost
          if (field === 'partQuantity' || field === 'unitPrice') {
            updated.totalPartCost = updated.partQuantity * updated.unitPrice;
          }
          return updated;
        }
        return item;
      });
      return { ...prev, serviceItems: updatedItems };
    });
    calculateTotal();
  };

  // Calculate total service cost
  const calculateTotal = () => {
    const partsCost = form.serviceItems.reduce((sum, item) => sum + item.totalPartCost, 0);
    const labour = parseFloat(form.labourCost) || 0;
    setForm(prev => ({
      ...prev,
      totalServiceCost: partsCost + labour
    }));
  };

  // Handle labour cost change
  const handleLabourCostChange = (value: string) => {
    setForm(prev => ({ ...prev, labourCost: value }));
    calculateTotal();
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedVehicle) return;

    // Calculate totals
    const totalPartsCost = form.serviceItems.reduce((sum, item) => sum + item.totalPartCost, 0);
    const labourCost = parseFloat(form.labourCost) || 0;
    const totalServiceCost = totalPartsCost + labourCost;

    const newService = {
      // Service Date
      spareChangedDate: form.spareChangedDate,
      serviceDate: form.spareChangedDate,

      // Service Items (new dynamic structure)
      serviceItems: form.serviceItems.map(item => ({
        serviceType: item.serviceType,
        partName: item.partName,
        partQuantity: item.partQuantity,
        unitPrice: item.unitPrice,
        totalPartCost: item.totalPartCost,
        serviceDescription: item.serviceDescription
      })),

      // Summary Costs
      totalPartsCost,
      labourCost,
      totalServiceCost,

      // Legacy fields (for backward compatibility)
      partDetails: form.serviceItems[0]?.partName || form.partDetails,
      amountOfParts: form.serviceItems.reduce((sum, item) => sum + item.partQuantity, 0),
      description: form.serviceItems.map(item => item.serviceDescription).join(', '),

      sparePreviousModifiedDate: form.sparePreviousModifiedDate,
      nextOdoCheckPoint: Number(form.nextOdoCheckPoint),
      changedBy: form.mechanicName || form.changedBy,
      remarks: form.remarks,
      status: form.status,

      // Additional fields
      odoReading: Number(form.odo),
      nextServiceDueKm: Number(form.nextServiceDueKm),
      nextServiceDueDate: form.nextServiceDueDate,
      mechanicName: form.mechanicName,
      serviceCenter: form.serviceCenter
    };

    try {
      const response = await addServiceHistoryApi({
        baseURL: `/vehicles/${selectedVehicle._id}/service`,
        payload: JSON.stringify(newService),
      });
      if (response && 'data' in response) {
        setForm(initialForm);
        setSnackbarMessage("Service added successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setTimeout(() => {
          navigate("/service-history");
        }, 1500);
      }
      if ('error' in response) {
        console.error("Error adding service:", response.error);
        setSnackbarMessage("Error adding service. Please try again.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Failed to add service:", error);
      setSnackbarMessage("Failed to add service. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Add New Service Details
      </Typography>
      <Box sx={{ maxWidth: 900, mx: "auto", mt: 4 }}>
        <Paper sx={{ p: 3 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Autocomplete
              options={vehicleList.map((v) => ({
                id: v._id,
                number: v.number,
                chasisNumber: v.transport?.chasisNumber?.toString() ?? "",
              }))}
              getOptionLabel={(option) => option.number}
              filterOptions={createFilterOptions<VehicleOption>({
                stringify: (option) => `${option.number} ${option.chasisNumber ?? ""}`,
              })}
              value={selectedVehicle}
              onChange={(_, value) => {
                const vehicle: any = vehicleList.find((v: any) => v._id === value?.id);
                if (vehicle) setSelectedVehicle(vehicle);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search & Select Vehicle"
                  placeholder="Type vehicle number or chassis number"
                  required
                  fullWidth
                  sx={{ mb: 3, backgroundColor: "white" }}
                />
              )}
            />
            {selectedVehicle && (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  {/* Vehicle Information Section */}
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'var(--primary-color)', mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                      Vehicle Information
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Current ODO Reading"
                      name="odo"
                      value={form.odo}
                      onChange={handleChange}
                      required
                      fullWidth
                      variant="outlined"
                      type="number"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DatePicker
                      label="Service Date"
                      value={form.spareChangedDate ? new Date(form.spareChangedDate) : null}
                      onChange={(date) => setForm({...form, spareChangedDate: date ? date.toISOString().split('T')[0] : ''})}
                      slotProps={{ textField: { fullWidth: true, variant: 'outlined' } }}
                    />
                  </Grid>

                  {/* Service Items Section - Dynamic */}
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'var(--primary-color)' }}>
                        Service Items
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={addServiceItem}
                        size="small"
                        sx={{ backgroundColor: 'var(--primary-color)' }}
                      >
                        Add Service Item
                      </Button>
                    </Box>
                  </Grid>

                  {/* Service Items List */}
                  {form.serviceItems.length === 0 ? (
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                        No service items added. Click "Add Service Item" to add parts/services.
                      </Typography>
                    </Grid>
                  ) : (
                    form.serviceItems.map((item, index) => (
                      <Grid size={{ xs: 12 }} key={item.id}>
                        <Paper variant="outlined" sx={{ p: 2, mb: 1, backgroundColor: '#f8f9fa' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              Service Item #{index + 1}
                            </Typography>
                            <IconButton size="small" onClick={() => removeServiceItem(item.id)} color="error">
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                          <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 4 }}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Service Type</InputLabel>
                                <Select
                                  value={item.serviceType}
                                  label="Service Type"
                                  onChange={(e) => updateServiceItem(item.id, 'serviceType', e.target.value)}
                                >
                                  {SERVICE_TYPES.map(type => (
                                    <MenuItem key={type} value={type}>{type}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                              <TextField
                                label="Part Name"
                                value={item.partName}
                                onChange={(e) => updateServiceItem(item.id, 'partName', e.target.value)}
                                fullWidth
                                size="small"
                              />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                              <TextField
                                label="Quantity"
                                type="number"
                                value={item.partQuantity}
                                onChange={(e) => updateServiceItem(item.id, 'partQuantity', parseInt(e.target.value) || 0)}
                                fullWidth
                                size="small"
                                inputProps={{ min: 1 }}
                              />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                              <TextField
                                label="Unit Price (₹)"
                                type="number"
                                value={item.unitPrice}
                                onChange={(e) => updateServiceItem(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                                fullWidth
                                size="small"
                                InputProps={{
                                  startAdornment: <Typography sx={{ mr: 0.5 }}>₹</Typography>
                                }}
                              />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                              <TextField
                                label="Total (₹)"
                                value={item.totalPartCost}
                                fullWidth
                                size="small"
                                disabled
                                InputProps={{
                                  startAdornment: <Typography sx={{ mr: 0.5 }}>₹</Typography>
                                }}
                              />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                              <TextField
                                label="Description"
                                value={item.serviceDescription}
                                onChange={(e) => updateServiceItem(item.id, 'serviceDescription', e.target.value)}
                                fullWidth
                                size="small"
                              />
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>
                    ))
                  )}

                  {/* Cost Summary */}
                  <Grid size={{ xs: 12 }}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'var(--primary-color)', mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0' }}>
                      Cost Summary
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Labour Cost (₹)"
                      name="labourCost"
                      value={form.labourCost}
                      onChange={(e) => handleLabourCostChange(e.target.value)}
                      fullWidth
                      type="number"
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 0.5 }}>₹</Typography>
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Total Service Cost (₹)"
                      value={form.totalServiceCost}
                      fullWidth
                      disabled
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 0.5, fontWeight: 600 }}>₹</Typography>
                      }}
                      sx={{ '& .MuiInputBase-input': { fontWeight: 600, fontSize: '1.1rem' } }}
                    />
                  </Grid>

                  {/* Service Details Section */}
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'var(--primary-color)', mb: 2, pb: 1, borderBottom: '1px solid #e0e0e0', mt: 2 }}>
                      Service Details
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Mechanic Name"
                      name="mechanicName"
                      value={form.mechanicName}
                      onChange={handleChange}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Service Center"
                      name="serviceCenter"
                      value={form.serviceCenter}
                      onChange={handleChange}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Next Service Due (KM)"
                      name="nextServiceDueKm"
                      value={form.nextServiceDueKm}
                      onChange={handleChange}
                      fullWidth
                      type="number"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DatePicker
                      label="Next Service Due Date"
                      value={form.nextServiceDueDate ? new Date(form.nextServiceDueDate) : null}
                      onChange={(date) => setForm({...form, nextServiceDueDate: date ? date.toISOString().split('T')[0] : ''})}
                      slotProps={{ textField: { fullWidth: true, variant: 'outlined' } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label="Remarks"
                      name="remarks"
                      value={form.remarks}
                      onChange={handleChange}
                      multiline
                      rows={2}
                      fullWidth
                    />
                  </Grid>

                  {/* Submit Button */}
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => navigate(-1)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        sx={{ backgroundColor: 'var(--primary-color)' }}
                      >
                        Add Service
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            )}
          </LocalizationProvider>
        </Paper>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default AddServiceDetails;
